/**
 * D-EZ-102 — THE TROPHY CASE on a driver's card.
 *
 * Who he is beyond the numbers on the card: his age and hometown, his titles
 * and awards (a Hall of Fame badge when he is in), the sponsors on his car and
 * how long they have been there, his career-best season, the milestones he has
 * reached, his rivalries, and — for a man no longer racing — how his career
 * ended. Every line is a fold over the league's seasons; nothing is stored.
 */

import { useMemo } from 'react';
import {
  allRaces,
  buildRegistry,
  careerMilestones,
  hallOfFame,
  rivalries,
  seasonAwards,
  type Award,
  type HistorySeason,
} from '../../domain/history/index.ts';
import type { FinanceState } from '../../domain/finance/raceFinance.ts';
import { sponsorSlotsFor } from '../../domain/finance/sponsorNames.ts';
import { hometownFor } from '../../domain/story/hometown.ts';
import { ordinal } from '../../domain/story/raceStory.ts';
import { careerEnding } from './HallOfFame.tsx';

export interface TrophyCaseProps {
  driverId: string;
  seasons: readonly HistorySeason[];
  /** The books as they stand, for the sponsors on his car. */
  contracts?: FinanceState | null;
  /** His age this season, when the card knows it. */
  age?: number | null;
}

export default function TrophyCase({ driverId, seasons, contracts, age }: TrophyCaseProps) {
  const data = useMemo(() => {
    const registry = buildRegistry(seasons);
    const entry = registry.byId[driverId] ?? null;
    const settled = seasons.filter((s) => s.complete !== false);
    const awards: (Award & { seasonNumber: number })[] = [];
    for (const s of settled) {
      for (const a of seasonAwards(seasons, s.seasonNumber, registry)) {
        if (a.driverId === driverId) awards.push({ ...a, seasonNumber: s.seasonNumber });
      }
    }
    const latest = seasons[seasons.length - 1]?.seasonNumber ?? 0;
    const inHall = hallOfFame(registry, latest + 1).find((h) => h.driverId === driverId) ?? null;
    const milestones = careerMilestones(allRaces(seasons)).filter(
      (m) => m.driverId === driverId && (m.kind === 'first-win' || m.kind === 'nth-win' || m.kind === 'nth-start' || m.kind === 'first-pole'),
    );
    const feuds = rivalries(seasons).filter((r) => r.a === driverId || r.b === driverId).slice(0, 3);
    const deaths = new Map<string, { seasonNumber: number; raceNumber: number }>();
    for (const s of seasons) for (const f of s.season.fatalities ?? []) deaths.set(f.driverId, { seasonNumber: s.seasonNumber, raceNumber: f.raceNumber });
    return { entry, awards, inHall, milestones, feuds, deaths };
  }, [seasons, driverId]);

  const { entry, awards, inHall, milestones, feuds, deaths } = data;
  // Sponsors are on a car that is still racing; a finished career has none.
  const racing = !entry || entry.status === 'active';
  const sponsors = useMemo(() => {
    const dfs = racing ? contracts?.byDriver[driverId] : undefined;
    return dfs ? sponsorSlotsFor(dfs) : [];
  }, [contracts, driverId, racing]);
  const hometown = hometownFor(driverId);
  const titles = entry?.bySeason.filter((l) => l.champion).map((l) => l.seasonNumber) ?? [];
  const nonTitle = awards.filter((a) => a.kind !== 'champion');
  const best = entry?.bestSeason ?? null;
  const shownAge = age ?? entry?.age ?? null;
  const former = entry && entry.status !== 'active';
  if (!entry && !hometown && sponsors.length === 0 && shownAge === null) return null;

  return (
    <section className="tcase" aria-label="Trophy case">
      <p className="tcase__line">
        {shownAge !== null && <span>Age {shownAge}</span>}
        {hometown && <span>from {hometown}</span>}
        {former && entry && <span className={`tcase__ended tcase__ended--${entry.status}`}>{careerEnding(entry, deaths)}</span>}
      </p>

      {(titles.length > 0 || inHall || nonTitle.length > 0) && (
        <ul className="tcase__trophies" aria-label="Titles and awards">
          {inHall && (
            <li className="tcase__badge tcase__badge--hof" title={`Inducted on ${inHall.citation}`}>
              Hall of Fame · class of season {inHall.inductedSeason}
            </li>
          )}
          {titles.map((n) => (
            <li key={`t${n}`} className="tcase__badge tcase__badge--title">
              ★ Champion · season {n}
            </li>
          ))}
          {nonTitle.map((a) => (
            <li key={`${a.kind}${a.seasonNumber}`} className="tcase__badge" title={a.basis}>
              {a.title} · S{a.seasonNumber}
            </li>
          ))}
        </ul>
      )}

      <div className="tcase__cols">
        {best && (
          <div className="tcase__col">
            <h4>Career-best season</h4>
            <p>
              Season {best.seasonNumber}: {best.champion ? 'champion' : `${ordinal(best.rank)} in the championship`}, {best.points} points,{' '}
              {best.wins} win{best.wins === 1 ? '' : 's'}
              {best.complete ? '' : ' (still being raced)'}.
            </p>
          </div>
        )}
        {sponsors.length > 0 && (
          <div className="tcase__col">
            <h4>On the car</h4>
            <ul className="tcase__list">
              {sponsors.slice(0, 4).map((s) => (
                <li key={`${s.slot}${s.name}`}>
                  <b>{s.name}</b> <span className="tcase__dim">· {s.slot} · {s.seasons === 1 ? 'first season' : `${ordinal(s.seasons)} season`}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {milestones.length > 0 && (
          <div className="tcase__col">
            <h4>Milestones</h4>
            <ul className="tcase__list">
              {milestones.slice(-4).map((m) => (
                <li key={`${m.kind}${m.n}${m.seasonNumber}${m.raceNumber}`}>
                  {m.kind === 'first-win'
                    ? 'First win'
                    : m.kind === 'nth-win'
                      ? `Win number ${m.n}`
                      : m.kind === 'first-pole'
                        ? 'First pole'
                        : `Start number ${m.n}`}{' '}
                  <span className="tcase__dim">· {m.raceName}, season {m.seasonNumber}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {feuds.length > 0 && (
          <div className="tcase__col">
            <h4>Feuds</h4>
            <ul className="tcase__list">
              {feuds.map((r) => (
                <li key={`${r.a}${r.b}`}>
                  {r.a === driverId ? r.bName : r.aName}{' '}
                  <span className="tcase__dim">
                    · {r.feuds} feud{r.feuds === 1 ? '' : 's'}, {r.races} race{r.races === 1 ? '' : 's'}
                    {r.live ? ' · still going' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
